import React, { useState, useEffect, useCallback, useRef } from 'react';
import './DesignPreview.css';

// Product image unavailable component
const ProductImageUnavailable = ({ colorName, currentView }) => {
  if (!colorName || colorName === 'Default') {
    return (
      <div className="product-image-placeholder">
        <span role="img" aria-label="palette" style={{ fontSize: '2em' }}>
          🎨
        </span>
        <br />
        <span>Please choose a product color to see the product preview</span>
      </div>
    );
  }

  return (
    <div className="product-image-placeholder">
      <span role="img" aria-label="camera" style={{ fontSize: '2em' }}>
        📸
      </span>
      <br />
      <span>
        We don't have a product photo for {colorName} ({currentView} view)
      </span>
      <br />
      <small>You can still view your design below</small>
    </div>
  );
};

// Helper to titleize strings (convert "ALL CAPS" to "All Caps")
const titleize = str => {
  if (!str) return str;
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper to ensure URL is absolute
const makeAbsoluteUrl = url => {
  if (!url) return null;
  if (
    url.startsWith('http://') ||
    url.startsWith('https://') ||
    url.startsWith('//')
  ) {
    return url;
  }
  // If it's a relative URL, prepend the origin
  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
};

const DesignPreview = ({ designData, hideCustomerDetails = false }) => {
  const [currentView, setCurrentView] = useState('front');
  const [productViews, setProductViews] = useState({});
  const [svgContentCache, setSvgContentCache] = useState({});
  const [loadingDesigns, setLoadingDesigns] = useState(new Set());
  const [pngDesigns, setPngDesigns] = useState(new Set());
  const [imageLoadError, setImageLoadError] = useState(false);
  const [preloadedImages, setPreloadedImages] = useState({});
  const [viewImageCache, setViewImageCache] = useState({});

  useEffect(() => {
    if (designData && designData.design_customization) {
      // Handle both snake_case (from Rails) and camelCase (from JS) keys
      const productViewsData =
        designData.design_customization.product_views ||
        designData.design_customization.productViews;
      if (productViewsData) {
        setProductViews(productViewsData);
      }

      const currentViewData =
        designData.design_customization.current_view ||
        designData.design_customization.currentView;
      if (currentViewData) {
        setCurrentView(currentViewData);
      }
    }
  }, [designData]);

  // Module-level cache to prevent race conditions
  const fetchPromiseCache = useRef({});

  // Fetch SVG content for a design
  const fetchSvgContent = useCallback(
    async designId => {
      // Check if already cached
      if (svgContentCache[designId]) {
        return svgContentCache[designId];
      }

      // Check if already loading - use ref cache to prevent race conditions
      if (fetchPromiseCache.current[designId]) {
        return fetchPromiseCache.current[designId];
      }

      // Create and cache the promise
      const fetchPromise = (async () => {
        // Mark as loading in state for UI feedback
        setLoadingDesigns(prev => new Set(prev).add(designId));

        try {
          const response = await fetch(`/designs/${designId}/svg_content`);
          const data = await response.json();

          if (data.success && data.svg_content) {
            setSvgContentCache(prev => ({
              ...prev,
              [designId]: data.svg_content,
            }));
            return data.svg_content;
          } else if (data.error && data.error.includes('not in SVG format')) {
            // Mark as PNG design
            setPngDesigns(prev => new Set([...prev, String(designId)]));
            return null;
          }
          return null;
        } catch (error) {
          return null;
        } finally {
          // Always clean up loading state and promise cache
          setLoadingDesigns(prev => {
            const newSet = new Set(prev);
            newSet.delete(designId);
            return newSet;
          });
          delete fetchPromiseCache.current[designId];
        }
      })();

      // Cache the promise to prevent duplicate requests
      fetchPromiseCache.current[designId] = fetchPromise;
      return fetchPromise;
    },
    [svgContentCache, setLoadingDesigns, setPngDesigns, setSvgContentCache]
  );

  // Effect to fetch all SVG content when component mounts
  useEffect(() => {
    if (!designData || !designData.design_customization) return;

    const fetchAllSvgContent = async () => {
      const designIds = new Set();

      // Collect all design IDs from side designs
      const sideDesigns =
        designData.design_customization.side_designs ||
        designData.design_customization.sideDesigns;
      if (sideDesigns) {
        Object.values(sideDesigns).forEach(designs => {
          if (Array.isArray(designs)) {
            designs.forEach(design => designIds.add(design.id));
          }
        });
      }

      // Collect design IDs from design pool
      const designPool =
        designData.design_customization.design_pool ||
        designData.design_customization.designPool;
      if (designPool && Array.isArray(designPool)) {
        designPool.forEach(design => designIds.add(design.id));
      }

      // Fetch SVG content for all designs
      const fetchPromises = Array.from(designIds).map(id =>
        fetchSvgContent(id)
      );
      await Promise.all(fetchPromises);
    };

    fetchAllSvgContent();
  }, [designData, fetchSvgContent]);

  const formatDate = dateString => {
    if (!dateString) return 'Not specified';
    // Parse the date string and add time to avoid timezone issues
    // If the date string is just YYYY-MM-DD, append time to treat as local
    const localDateString = dateString.includes('T')
      ? dateString
      : `${dateString}T00:00:00`;
    const date = new Date(localDateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getViewImage = useCallback(
    view => {
      if (!designData || !designData.product_color) {
        return null;
      }
      const product_color = designData.product_color;
      const product = designData.product;

      // Handle both snake_case and camelCase
      const marketingImageUrl =
        product_color.marketing_image_url || product_color.marketingImageUrl;

      // Check if productViews has the expected structure
      let imageUrl = null;
      let colorViews = null;

      if (productViews && Object.keys(productViews).length > 0) {
        const colorNameLower = product_color.name
          ? product_color.name.toLowerCase()
          : '';

        // Try different variations of the color name
        const colorVariations = [
          product_color.name,
          colorNameLower,
          colorNameLower.replace(/\s+/g, '_'),
          colorNameLower.replace(/\s+/g, '-'),
        ];

        for (const variation of colorVariations) {
          if (productViews[variation]) {
            colorViews = productViews[variation];
            break;
          }
        }

        // If still not found, check all keys case-insensitively
        if (!colorViews) {
          const matchingKey = Object.keys(productViews).find(
            key => key.toLowerCase() === colorNameLower
          );
          if (matchingKey) {
            colorViews = productViews[matchingKey];
          }
        }

        if (colorViews && colorViews[view]) {
          imageUrl = colorViews[view];
        } else if (colorViews) {
          // Try the old property name structure
          switch (view) {
            case 'front':
              imageUrl =
                colorViews.color_front_image ||
                colorViews.colorFrontImage ||
                colorViews.front;
              break;
            case 'side':
              imageUrl =
                colorViews.color_direct_side_image ||
                colorViews.colorDirectSideImage ||
                colorViews.color_side_image ||
                colorViews.colorSideImage ||
                colorViews.color_left_image ||
                colorViews.colorLeftImage ||
                colorViews.color_right_image ||
                colorViews.colorRightImage ||
                colorViews.side ||
                colorViews.left ||
                colorViews.right;
              break;
            case 'back':
              imageUrl =
                colorViews.color_back_image ||
                colorViews.colorBackImage ||
                colorViews.back;
              break;
            default:
              break;
          }
        }
      }

      // Fallback to marketing image ONLY for front view (not for back/side)
      if (!imageUrl && view === 'front') {
        imageUrl =
          marketingImageUrl ||
          (product && product.image_primary) ||
          (product && product.image_secondary) ||
          (product && product.image_interest);
      }

      return makeAbsoluteUrl(imageUrl);
    },
    [designData, productViews]
  );

  const handleViewChange = useCallback(view => {
    setCurrentView(view);
    setImageLoadError(false);
  }, []);

  // Check if a specific view has an actual image (not a fallback)
  const hasViewImage = useCallback(
    view => {
      if (!designData || !designData.product_color) return false;

      const product_color = designData.product_color;
      let colorViews = null;

      if (productViews && Object.keys(productViews).length > 0) {
        const colorNameLower = product_color.name
          ? product_color.name.toLowerCase()
          : '';

        // Try different variations of the color name
        const colorVariations = [
          product_color.name,
          colorNameLower,
          colorNameLower.replace(/\s+/g, '_'),
          colorNameLower.replace(/\s+/g, '-'),
        ];

        for (const variation of colorVariations) {
          if (productViews[variation]) {
            colorViews = productViews[variation];
            break;
          }
        }

        // If still not found, check all keys case-insensitively
        if (!colorViews) {
          const matchingKey = Object.keys(productViews).find(
            key => key.toLowerCase() === colorNameLower
          );
          if (matchingKey) {
            colorViews = productViews[matchingKey];
          }
        }
      }

      if (!colorViews) return view === 'front'; // Only front view available with marketing image

      // Check if this specific view has an image
      switch (view) {
        case 'front':
          return !!(
            colorViews[view] ||
            colorViews.color_front_image ||
            colorViews.colorFrontImage ||
            colorViews.front
          );
        case 'side':
          return !!(
            colorViews[view] ||
            colorViews.color_direct_side_image ||
            colorViews.colorDirectSideImage ||
            colorViews.color_side_image ||
            colorViews.colorSideImage ||
            colorViews.color_left_image ||
            colorViews.colorLeftImage ||
            colorViews.color_right_image ||
            colorViews.colorRightImage ||
            colorViews.side ||
            colorViews.left ||
            colorViews.right
          );
        case 'back':
          return !!(
            colorViews[view] ||
            colorViews.color_back_image ||
            colorViews.colorBackImage ||
            colorViews.back
          );
        default:
          return false;
      }
    },
    [designData, productViews]
  );

  const getCurrentViewImage = useCallback(() => {
    if (!designData || !designData.product_color) return null;
    // Use cached image URL if available for instant switching
    if (viewImageCache[currentView]) {
      return viewImageCache[currentView];
    }
    return getViewImage(currentView);
  }, [currentView, viewImageCache, designData, getViewImage]);

  // Preload all view images when component mounts or data changes
  useEffect(() => {
    if (!designData || !designData.product_color) return;

    const views = ['front', 'side', 'back'];
    const preloadPromises = [];
    const newViewImageCache = {};

    views.forEach(view => {
      const imageUrl = getViewImage(view);
      newViewImageCache[view] = imageUrl;

      if (imageUrl && !preloadedImages[imageUrl]) {
        // Create image element to preload
        const img = new Image();

        const promise = new Promise(resolve => {
          img.onload = () => {
            setPreloadedImages(prev => ({ ...prev, [imageUrl]: true }));
            resolve();
          };
          img.onerror = () => {
            resolve(); // Resolve even on error to continue
          };
        });

        // Start loading the image
        img.src = imageUrl;
        preloadPromises.push(promise);
      }
    });

    // Cache the view URLs
    setViewImageCache(newViewImageCache);

    // Wait for all images to load
    Promise.all(preloadPromises).then(() => {});
  }, [productViews, designData, preloadedImages, getViewImage]);

  // Early return after all hooks
  if (!designData) {
    return (
      <div className="design-preview-loading">Loading design preview...</div>
    );
  }

  const {
    name,
    email,
    phone_number,
    business_name,
    school,
    organization,
    event_name,
    quantity,
    delivery_date,
    budget,
    order_notes,
    design_notes,
    product,
    product_color,
    design_customization,
  } = designData;

  // Helper to check if there are any designs to display on the current view
  const hasDesignsOnCurrentView = () => {
    if (!design_customization) return false;
    const sideDesignsData =
      design_customization.side_designs || design_customization.sideDesigns;
    if (!sideDesignsData) return false;
    const currentSideDesigns = sideDesignsData[currentView] || [];
    return currentSideDesigns.length > 0;
  };

  const renderDesignOnProduct = () => {
    if (!design_customization) return null;

    // Handle both snake_case and camelCase keys
    const sideDesignsData =
      design_customization.side_designs || design_customization.sideDesigns;

    // Also check the design pool if no designs on sides
    const designPool =
      design_customization.design_pool || design_customization.designPool || [];

    if (!sideDesignsData && designPool.length === 0) return null;

    const currentSideDesigns =
      (sideDesignsData && sideDesignsData[currentView]) || [];

    // Handle different possible keys for positions and sizes
    const designPositions =
      design_customization.design_positions ||
      design_customization.designPositions ||
      design_customization.positions ||
      {};
    const designSizes =
      design_customization.design_sizes ||
      design_customization.designSizes ||
      design_customization.sizes ||
      {};
    const sideSvgContent =
      design_customization.side_svg_content ||
      design_customization.sideSvgContent ||
      {};
    const sideColorMappings =
      design_customization.side_color_mappings ||
      design_customization.sideColorMappings ||
      {};

    return currentSideDesigns.map(design => {
      // Try both underscore and dash formats for the key
      const designKeyUnderscore = `${currentView}_${design.id}`;
      const designKeyDash = `${currentView}-${design.id}`;

      // Check both key formats for position and size
      const position = designPositions[designKeyUnderscore] ||
        designPositions[designKeyDash] || { x: 50, y: 50 };
      const size = designSizes[designKeyUnderscore] ||
        designSizes[designKeyDash] || { width: 40, height: 40 };

      // Try to get SVG content from the fetched cache first
      let svgContent = svgContentCache[design.id];

      // Fallback to existing data if available
      if (
        !svgContent &&
        sideSvgContent[currentView] &&
        sideSvgContent[currentView][design.id]
      ) {
        svgContent = sideSvgContent[currentView][design.id];
      }

      // Check if this is a PNG design
      const isPngDesign =
        pngDesigns.has(String(design.id)) ||
        (design.thumbnailUrl &&
          design.thumbnailUrl.toLowerCase().includes('.png')) ||
        (design.thumbnail_url &&
          design.thumbnail_url.toLowerCase().includes('.png')) ||
        (design.url && design.url.toLowerCase().includes('.png'));

      // Apply color mappings to the SVG content
      const colorMappings =
        sideColorMappings[currentView] &&
        sideColorMappings[currentView][design.id];
      if (colorMappings && svgContent) {
        // Apply each color mapping
        Object.entries(colorMappings).forEach(([original, replacement]) => {
          if (original !== replacement) {
            // Create color variants (with and without #, uppercase and lowercase)
            const variants = [];

            // Handle different color formats
            if (original.startsWith('#')) {
              variants.push(original.toLowerCase());
              variants.push(original.toUpperCase());
              variants.push(original.substring(1).toLowerCase());
              variants.push(original.substring(1).toUpperCase());
            } else {
              variants.push(original.toLowerCase());
              variants.push(original.toUpperCase());
              variants.push('#' + original.toLowerCase());
              variants.push('#' + original.toUpperCase());
            }

            // Handle special case for IDs with underscores (like f_9_d_3_e_2)
            // These often represent hex colors with underscores instead of the actual hex format
            if (original.includes('_')) {
              variants.push(original);
              const hexFromUnderscore = original.replace(/_/g, '');
              variants.push(hexFromUnderscore);
              variants.push('#' + hexFromUnderscore);
              variants.push('#' + hexFromUnderscore.toLowerCase());
              variants.push('#' + hexFromUnderscore.toUpperCase());

              // Also try the hex format that might be in the SVG
              // f_9_d_3_e_2 -> #f9d3e2
              const possibleHex = original.split('_').join('');
              if (possibleHex.match(/^[0-9a-fA-F]{6}$/)) {
                variants.push('#' + possibleHex.toLowerCase());
                variants.push('#' + possibleHex.toUpperCase());
                variants.push(possibleHex.toLowerCase());
                variants.push(possibleHex.toUpperCase());
              }
            }

            variants.forEach(variant => {
              const replaceValue =
                replacement === 'transparent' ? 'transparent' : replacement;

              // Replace in fill attributes
              svgContent = svgContent.replace(
                new RegExp(`fill="${variant}"`, 'gi'),
                `fill="${replaceValue}"`
              );

              // Replace in style attributes
              svgContent = svgContent.replace(
                new RegExp(`fill:${variant}`, 'gi'),
                `fill:${replaceValue}`
              );

              // Replace in id-based fills (for gradients or patterns)
              if (variant.match(/^[a-zA-Z0-9_]+$/)) {
                svgContent = svgContent.replace(
                  new RegExp(`fill="url\\(#${variant}\\)"`, 'gi'),
                  `fill="${replaceValue}"`
                );
              }
            });
          }
        });
      }

      // Always remove backgrounds from SVG content
      if (svgContent) {
        // Helper to detect background color
        const detectBackgroundColor = svgText => {
          // Look for the first rect element with a fill color
          const rectMatch = svgText.match(/<rect[^>]*fill="([^"]+)"[^>]*>/);
          if (
            rectMatch &&
            rectMatch[1] &&
            rectMatch[1] !== 'none' &&
            rectMatch[1] !== 'transparent'
          ) {
            // Check if this rect is likely a background (has 100% width/height or covers viewBox)
            const rectTag = rectMatch[0];
            if (
              rectTag.includes('width="100%"') ||
              rectTag.includes('height="100%"') ||
              rectTag.includes('width="972"') ||
              rectTag.includes('height="1152"')
            ) {
              return rectMatch[1];
            }
          }

          // Look for large rectangles that likely represent backgrounds
          const viewBoxMatch = svgText.match(/viewBox="0\s+0\s+(\d+)\s+(\d+)"/);
          if (viewBoxMatch) {
            const viewBoxWidth = parseInt(viewBoxMatch[1]);
            const viewBoxHeight = parseInt(viewBoxMatch[2]);

            // Look for rectangles that cover at least 80% of the viewBox
            const largeRectRegex = /<rect[^>]*width="(\d+)"[^>]*height="(\d+)"[^>]*fill="([^"]+)"[^>]*>/g;
            let match;
            while ((match = largeRectRegex.exec(svgText)) !== null) {
              const width = parseInt(match[1]);
              const height = parseInt(match[2]);
              const fill = match[3];

              if (
                fill !== 'none' &&
                fill !== 'transparent' &&
                width >= viewBoxWidth * 0.8 &&
                height >= viewBoxHeight * 0.8
              ) {
                return fill;
              }
            }
          }

          return null;
        };

        const bgColor = detectBackgroundColor(svgContent);
        if (bgColor) {
          // Apply background removal for detected color
          const variants = [];
          if (bgColor.startsWith('#')) {
            variants.push(bgColor.toLowerCase());
            variants.push(bgColor.toUpperCase());
            variants.push(bgColor.substring(1).toLowerCase());
            variants.push(bgColor.substring(1).toUpperCase());
          } else {
            variants.push(bgColor.toLowerCase());
            variants.push(bgColor.toUpperCase());
            variants.push('#' + bgColor.toLowerCase());
            variants.push('#' + bgColor.toUpperCase());
          }

          variants.forEach(variant => {
            svgContent = svgContent.replace(
              new RegExp(`fill="${variant}"`, 'gi'),
              'fill="transparent"'
            );
            svgContent = svgContent.replace(
              new RegExp(`fill:${variant}`, 'gi'),
              'fill: transparent'
            );
          });
        }

        // Also apply pattern-based background removal
        // Method 1: Replace fill in Apparel_Color rect elements
        svgContent = svgContent.replace(
          /(<g\s+id="Apparel_Color"[^>]*>[\s\S]*?<rect[^>]+)fill="[^"]*"/gi,
          '$1fill="transparent"'
        );

        // Method 2: First rect with specific dimensions
        svgContent = svgContent.replace(
          /(<rect[^>]+width="972"[^>]+height="1152"[^>]+)fill="[^"]*"/gi,
          '$1fill="transparent"'
        );

        // Method 3: Background rect pattern
        svgContent = svgContent.replace(
          /<rect\s+x="-[\d.]+"[^>]+width="972"[^>]+fill="#[^"]+"/gi,
          match => match.replace(/fill="#[^"]+"/, 'fill="transparent"')
        );

        // Method 4: First large rect that covers most of the viewBox
        svgContent = svgContent.replace(
          /<rect\s+(?:x="0"\s+)?(?:y="0"\s+)?width="100%"\s+height="100%"[^>]*fill="[^"]+"/gi,
          match => match.replace(/fill="[^"]+"/, 'fill="transparent"')
        );
      }

      return (
        <div
          key={designKeyUnderscore}
          className="design-overlay-readonly"
          style={{
            position: 'absolute',
            left: `${position.x}%`,
            top: `${position.y}%`,
            width: `${size.width}%`,
            height: `${size.height}%`,
            transform: 'translate(-50%, -50%)',
          }}
        >
          {isPngDesign || !svgContent ? (
            <img
              src={makeAbsoluteUrl(
                design.thumbnailUrl ||
                  design.thumbnail_url ||
                  design.url ||
                  design.fullsizeUrl ||
                  design.fullsize_url ||
                  `/designs/${design.id}/image`
              )}
              alt={design.artCode || design.art_code || `Design ${design.id}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          ) : (
            <div
              className="svg-content-readonly"
              dangerouslySetInnerHTML={{ __html: svgContent }}
            />
          )}
        </div>
      );
    });
  };

  // Standard ink colors mapping
  const standardInkColors = [
    // Reds
    { name: 'Red', hex: '#EB0028' },
    { name: 'Cardinal Red', hex: '#A02741' },
    { name: 'Maroon', hex: '#73243C' },
    { name: 'Watermelon', hex: '#F15A5F' },
    { name: 'Coral', hex: '#FF8671' },
    // Oranges
    { name: 'Orange', hex: '#FF5000' },
    { name: 'Tiger Orange', hex: '#F58220' },
    { name: 'Melon', hex: '#FAAE61' },
    { name: 'Light Peach', hex: '#EBC2B2' },
    // Yellows
    { name: 'Yellow', hex: '#FCD732' },
    { name: 'Yellow Gold', hex: '#FFB718' },
    { name: 'Old Gold', hex: '#AF841D' },
    { name: 'Pale Yellow', hex: '#EDE499' },
    // Greens
    { name: 'Fluo Green', hex: '#00D24B' },
    { name: 'Bright Green', hex: '#82BC00' },
    { name: 'Lime Green', hex: '#009A39' },
    { name: 'Kelly Green', hex: '#006649' },
    { name: 'Dark Green', hex: '#00514B' },
    { name: 'Mint Green', hex: '#A0D8B3' },
    { name: 'Olive Green', hex: '#85B09A' },
    // Blues
    { name: 'Bright Blue', hex: '#00A8DA' },
    { name: 'Royal Blue', hex: '#005EB8' },
    { name: 'Navy', hex: '#003865' },
    { name: 'Columbia Blue', hex: '#72C0F1' },
    { name: 'Light Blue', hex: '#92C1E9' },
    // Purples
    { name: 'Purple', hex: '#6B3FA0' },
    { name: 'Dark Purple', hex: '#4B2D73' },
    { name: 'Light Purple', hex: '#8E6F9E' },
    // Pinks
    { name: 'Hot Pink', hex: '#ED0181' },
    { name: 'Pink', hex: '#FFB3D0' },
    { name: 'Light Pink', hex: '#FFCCE5' },
    // Browns
    { name: 'Brown', hex: '#5C3D31' },
    { name: 'Light Brown', hex: '#A67C52' },
    // Grays/Black/White
    { name: 'Black', hex: '#000000' },
    { name: 'Dark Grey', hex: '#56585A' },
    { name: 'Medium Grey', hex: '#7C878E' },
    { name: 'Light Grey', hex: '#A3AAAE' },
    { name: 'Cream', hex: '#EBE3D7' },
    { name: 'Basic White', hex: '#F9F9F9' },
  ];

  // Create hex to color name lookup
  const hexToColorName = {};
  standardInkColors.forEach(color => {
    hexToColorName[color.hex.toUpperCase()] = color.name;
  });
  hexToColorName['#FFFFFF'] = 'Basic White';
  hexToColorName['#FFF'] = 'Basic White';

  // Extract colors from SVG content
  const extractColorsFromSVG = svgText => {
    const colors = new Set();

    // Match fill colors in various formats
    const fillMatches = svgText.match(/fill="([^"]+)"/gi) || [];
    fillMatches.forEach(match => {
      const colorMatch = match.match(/fill="([^"]+)"/i);
      const color = colorMatch && colorMatch[1];
      if (
        color &&
        color !== 'none' &&
        color !== 'transparent' &&
        !color.includes('url(')
      ) {
        colors.add(color);
      }
    });

    // Match fill in style attributes
    const styleMatches = svgText.match(/fill:\s*([^;]+)/gi) || [];
    styleMatches.forEach(match => {
      const colorMatch = match.match(/fill:\s*([^;]+)/i);
      const color = colorMatch && colorMatch[1];
      if (
        color &&
        color !== 'none' &&
        color !== 'transparent' &&
        !color.includes('url(')
      ) {
        colors.add(color.trim());
      }
    });

    return Array.from(colors);
  };

  const renderInkColors = () => {
    if (!design_customization) return null;

    const sideDesignsData =
      design_customization.side_designs || design_customization.sideDesigns;
    const sideSvgContent =
      design_customization.side_svg_content ||
      design_customization.sideSvgContent ||
      {};
    const sideColorMappings =
      design_customization.side_color_mappings ||
      design_customization.sideColorMappings ||
      {};

    // Extract all unique colors from all designs
    const allColors = new Set();

    if (sideDesignsData) {
      Object.entries(sideDesignsData).forEach(([side, designs]) => {
        if (Array.isArray(designs)) {
          designs.forEach(design => {
            // Skip PNG designs - they don't have extractable ink colors
            if (pngDesigns.has(String(design.id))) {
              return;
            }

            // Try to get SVG content from cache first, then from sideSvgContent
            let svgContent =
              svgContentCache[design.id] ||
              (sideSvgContent[side] && sideSvgContent[side][design.id]);

            if (svgContent) {
              const colorMappings =
                sideColorMappings[side] && sideColorMappings[side][design.id];

              // Apply color mappings to the SVG content (same logic as renderDesignOnProduct)
              if (colorMappings) {
                Object.entries(colorMappings).forEach(
                  ([original, replacement]) => {
                    if (original !== replacement) {
                      // Create color variants
                      const variants = [];

                      if (original.startsWith('#')) {
                        variants.push(original.toLowerCase());
                        variants.push(original.toUpperCase());
                        variants.push(original.substring(1).toLowerCase());
                        variants.push(original.substring(1).toUpperCase());
                      } else {
                        variants.push(original.toLowerCase());
                        variants.push(original.toUpperCase());
                        variants.push('#' + original.toLowerCase());
                        variants.push('#' + original.toUpperCase());
                      }

                      if (original.includes('_')) {
                        const hexFromUnderscore = original.replace(/_/g, '');
                        variants.push(hexFromUnderscore);
                        variants.push('#' + hexFromUnderscore);
                        variants.push('#' + hexFromUnderscore.toLowerCase());
                        variants.push('#' + hexFromUnderscore.toUpperCase());
                      }

                      variants.forEach(variant => {
                        const replaceValue =
                          replacement === 'transparent'
                            ? 'transparent'
                            : replacement;
                        svgContent = svgContent.replace(
                          new RegExp(`fill="${variant}"`, 'gi'),
                          `fill="${replaceValue}"`
                        );
                        svgContent = svgContent.replace(
                          new RegExp(`fill:${variant}`, 'gi'),
                          `fill:${replaceValue}`
                        );
                      });
                    }
                  }
                );
              }

              // Always apply automatic background removal
              if (svgContent) {
                // Helper to detect background color
                const detectBackgroundColor = svgText => {
                  // Look for the first rect element with a fill color
                  const rectMatch = svgText.match(
                    /<rect[^>]*fill="([^"]+)"[^>]*>/
                  );
                  if (
                    rectMatch &&
                    rectMatch[1] &&
                    rectMatch[1] !== 'none' &&
                    rectMatch[1] !== 'transparent'
                  ) {
                    // Check if this rect is likely a background (has 100% width/height or covers viewBox)
                    const rectTag = rectMatch[0];
                    if (
                      rectTag.includes('width="100%"') ||
                      rectTag.includes('height="100%"') ||
                      rectTag.includes('width="972"') ||
                      rectTag.includes('height="1152"')
                    ) {
                      return rectMatch[1];
                    }
                  }

                  // Look for large rectangles that likely represent backgrounds
                  const viewBoxMatch = svgText.match(
                    /viewBox="0\s+0\s+(\d+)\s+(\d+)"/
                  );
                  if (viewBoxMatch) {
                    const viewBoxWidth = parseInt(viewBoxMatch[1]);
                    const viewBoxHeight = parseInt(viewBoxMatch[2]);

                    // Look for rectangles that cover at least 80% of the viewBox
                    const largeRectRegex = /<rect[^>]*width="(\d+)"[^>]*height="(\d+)"[^>]*fill="([^"]+)"[^>]*>/g;
                    let match;
                    while ((match = largeRectRegex.exec(svgText)) !== null) {
                      const width = parseInt(match[1]);
                      const height = parseInt(match[2]);
                      const fill = match[3];

                      if (
                        fill !== 'none' &&
                        fill !== 'transparent' &&
                        width >= viewBoxWidth * 0.8 &&
                        height >= viewBoxHeight * 0.8
                      ) {
                        return fill;
                      }
                    }
                  }

                  return null;
                };

                const bgColor = detectBackgroundColor(svgContent);
                if (bgColor) {
                  // Apply background removal for detected color
                  const variants = [];
                  if (bgColor.startsWith('#')) {
                    variants.push(bgColor.toLowerCase());
                    variants.push(bgColor.toUpperCase());
                    variants.push(bgColor.substring(1).toLowerCase());
                    variants.push(bgColor.substring(1).toUpperCase());
                  } else {
                    variants.push(bgColor.toLowerCase());
                    variants.push(bgColor.toUpperCase());
                    variants.push('#' + bgColor.toLowerCase());
                    variants.push('#' + bgColor.toUpperCase());
                  }

                  variants.forEach(variant => {
                    svgContent = svgContent.replace(
                      new RegExp(`fill="${variant}"`, 'gi'),
                      'fill="transparent"'
                    );
                    svgContent = svgContent.replace(
                      new RegExp(`fill:${variant}`, 'gi'),
                      'fill: transparent'
                    );
                  });
                }

                // Also apply pattern-based background removal
                // Method 1: Replace fill in Apparel_Color rect elements
                svgContent = svgContent.replace(
                  /(<g\s+id="Apparel_Color"[^>]*>[\s\S]*?<rect[^>]+)fill="[^"]*"/gi,
                  '$1fill="transparent"'
                );

                // Method 2: First rect with specific dimensions
                svgContent = svgContent.replace(
                  /(<rect[^>]+width="972"[^>]+height="1152"[^>]+)fill="[^"]*"/gi,
                  '$1fill="transparent"'
                );

                // Method 3: Background rect pattern
                svgContent = svgContent.replace(
                  /<rect\s+x="-[\d.]+"[^>]+width="972"[^>]+fill="#[^"]+"/gi,
                  match => match.replace(/fill="#[^"]+"/, 'fill="transparent"')
                );

                // Method 4: First large rect that covers most of the viewBox
                svgContent = svgContent.replace(
                  /<rect\s+(?:x="0"\s+)?(?:y="0"\s+)?width="100%"\s+height="100%"[^>]*fill="[^"]+"/gi,
                  match => match.replace(/fill="[^"]+"/, 'fill="transparent"')
                );
              }

              // Now extract colors from the modified SVG
              const svgColors = extractColorsFromSVG(svgContent);
              svgColors.forEach(color => {
                if (color !== 'transparent' && !color.startsWith('url(')) {
                  // Normalize color before adding to ensure uniqueness
                  let normalizedColor = color.toUpperCase();
                  if (!normalizedColor.startsWith('#')) {
                    normalizedColor = `#${normalizedColor}`;
                  }

                  // Convert 3-char hex to 6-char hex (#FFF -> #FFFFFF)
                  if (normalizedColor.match(/^#[0-9A-F]{3}$/)) {
                    normalizedColor = `#${normalizedColor[1]}${
                      normalizedColor[1]
                    }${normalizedColor[2]}${normalizedColor[2]}${
                      normalizedColor[3]
                    }${normalizedColor[3]}`;
                  }

                  allColors.add(normalizedColor);
                }
              });
            }
          });
        }
      });
    }

    // Convert colors to array and get color names
    const uniqueColors = Array.from(allColors).map(hex => {
      // Colors are already normalized (uppercase with #)
      // Try to find the color name
      const colorName = hexToColorName[hex];

      return {
        hex: hex,
        name: colorName || null,
      };
    });

    // Check if all designs are non-customizable (PNG or large SVG)
    let allDesignsArePng = false;
    if (sideDesignsData) {
      const allDesignIds = [];
      Object.values(sideDesignsData).forEach(designs => {
        if (Array.isArray(designs)) {
          designs.forEach(design => allDesignIds.push(String(design.id)));
        }
      });
      allDesignsArePng =
        allDesignIds.length > 0 && allDesignIds.every(id => pngDesigns.has(id));
    }

    if (uniqueColors.length === 0 && !allDesignsArePng) return null;

    return (
      <div className="ink-colors-summary">
        <h3>Design Details</h3>

        {/* Ink colors */}
        {uniqueColors.length > 0 ? (
          <>
            <div className="ink-colors-header">
              <h4>Colors ({uniqueColors.length})</h4>
            </div>
            <div className="ink-color-list">
              {uniqueColors.map((color, index) => (
                <div key={index} className="ink-color-item">
                  <div
                    className="ink-color-swatch"
                    style={{ backgroundColor: color.hex }}
                  />
                  <span className="ink-color-hex mlm">
                    {color.hex}
                    {color.name && ` (${color.name})`}
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : allDesignsArePng ? (
          <div className="design-note">
            <p className="note-text">
              <strong>Note:</strong> Ink colors cannot be extracted from PNG
              designs.
            </p>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="design-preview-container">
      <div
        className={`preview-grid ${hideCustomerDetails ? 'single-column' : ''}`}
      >
        {/* Design Preview Section */}
        <div className="preview-section design-preview">
          <h2>Design Preview</h2>

          {/* View Selector */}
          <div className="view-selector-readonly">
            {['front', 'back', 'side'].map(view => {
              const sideDesignsData =
                design_customization &&
                (design_customization.side_designs ||
                  design_customization.sideDesigns);
              const hasDesigns =
                sideDesignsData &&
                sideDesignsData[view] &&
                sideDesignsData[view].length > 0;

              const hasImage = hasViewImage(view);
              // Only disable if there's no image AND no designs on that view
              const isDisabled =
                !hasImage && !hasDesigns && (view === 'side' || view === 'back');

              return (
                <button
                  key={view}
                  className={`view-button-readonly ${
                    currentView === view ? 'active' : ''
                  } ${isDisabled ? 'disabled' : ''}`}
                  onClick={() => !isDisabled && handleViewChange(view)}
                  disabled={isDisabled}
                  title={isDisabled ? `No ${view} view available` : ''}
                >
                  {view.charAt(0).toUpperCase() + view.slice(1)}
                  {hasDesigns && <span className="design-indicator">●</span>}
                </button>
              );
            })}
          </div>

          {/* Product Mockup */}
          <div className="product-mockup-readonly">
            {product && product_color ? (
              getCurrentViewImage() && !imageLoadError ? (
                <img
                  src={getCurrentViewImage()}
                  alt={`${product_color.name} - ${currentView} view`}
                  className="product-image-readonly"
                  onError={() => {
                    setImageLoadError(true);
                  }}
                  onLoad={() => setImageLoadError(false)}
                />
              ) : (
                <div className="product-image-placeholder" style={hasDesignsOnCurrentView() ? { opacity: 0 } : {}}>
                  <ProductImageUnavailable
                    colorName={product_color.name}
                    currentView={currentView}
                  />
                </div>
              )
            ) : (
              <div className="product-image-placeholder" style={hasDesignsOnCurrentView() ? { opacity: 0 } : {}}>
                <span>No product selected</span>
              </div>
            )}
            {renderDesignOnProduct()}
          </div>

          {/* Ink Colors */}
          {renderInkColors()}

          {/* Note about unplaced designs */}
          {(() => {
            const designPool =
              design_customization &&
              (design_customization.design_pool ||
                design_customization.designPool);
            const hasUnplacedDesigns = designPool && designPool.length > 0;
            const sideDesigns =
              design_customization &&
              (design_customization.side_designs ||
                design_customization.sideDesigns);
            const hasPlacedDesigns =
              sideDesigns &&
              ((sideDesigns.front && sideDesigns.front.length > 0) ||
                (sideDesigns.back && sideDesigns.back.length > 0) ||
                (sideDesigns.side && sideDesigns.side.length > 0));

            if (hasUnplacedDesigns && !hasPlacedDesigns) {
              return (
                <div className="design-note">
                  <p className="note-text">
                    <strong>Note:</strong> Customer selected designs but did not
                    place them on the product. See "Selected Designs" section
                    below.
                  </p>
                </div>
              );
            }
            return null;
          })()}

          {/* Selected Designs Section */}
          <h3>Selected Designs</h3>
          {design_customization &&
            (design_customization.design_pool ||
              design_customization.designPool) &&
            (
              design_customization.design_pool ||
              design_customization.designPool
            ).length > 0 ? (
              <div className="selected-designs-section">
                <div className="design-pool-readonly">
                  {(
                    design_customization.design_pool ||
                    design_customization.designPool
                  ).map(design => {
                    // Get SVG content from cache
                    const designSvgContent = svgContentCache[design.id];

                    // Create the direct design link using the ID
                    const designGalleryUrl = `/designs/${design.id}`;

                    return (
                      <a
                        key={design.id}
                        href={designGalleryUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="design-item-readonly design-item-link"
                        title={`View ${design.art_code ||
                          design.artCode ||
                          `Design ${design.id}`} in gallery`}
                      >
                        {designSvgContent ? (
                          <div
                            className="design-svg-thumbnail"
                            dangerouslySetInnerHTML={{
                              __html: designSvgContent,
                            }}
                          />
                        ) : pngDesigns.has(String(design.id)) ? (
                          // Show PNG thumbnail
                          <div className="design-png-thumbnail">
                            {design.thumbnailUrl ||
                            design.thumbnail_url ||
                            design.url ? (
                              <img
                                src={makeAbsoluteUrl(
                                  design.thumbnailUrl ||
                                    design.thumbnail_url ||
                                    design.url
                                )}
                                alt={
                                  design.art_code ||
                                  design.artCode ||
                                  `Design ${design.id}`
                                }
                                onError={e => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = `<span class="design-icon" role="img" aria-label="art">🎨</span><span>${design.art_code ||
                                    design.artCode ||
                                    `Design ${design.id}`}</span>`;
                                }}
                              />
                            ) : (
                              <div className="design-placeholder">
                                <span
                                  className="design-icon"
                                  role="img"
                                  aria-label="art"
                                >
                                  🎨
                                </span>
                                <span>
                                  {design.art_code ||
                                    design.artCode ||
                                    `Design ${design.id}`}
                                </span>
                              </div>
                            )}
                          </div>
                        ) : loadingDesigns.has(design.id) ? (
                          <div className="design-placeholder">
                            <div className="loading-spinner" />
                          </div>
                        ) : (
                          <div className="design-placeholder">
                            <span
                              className="design-icon"
                              role="img"
                              aria-label="art"
                            >
                              🎨
                            </span>
                            <span>
                              {design.art_code ||
                                design.artCode ||
                                `Design ${design.id}`}
                            </span>
                          </div>
                        )}
                        <span className="design-label">
                          {design.art_code ||
                            design.artCode ||
                            `Design ${design.id}`}
                        </span>
                      </a>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="no-designs-message">
                <p>No designs selected</p>
              </div>
            )}
        </div>

        {/* Right Column - Customer & Order Details */}
        {!hideCustomerDetails && (
          <div className="preview-section order-details">
            {/* Customer Information */}
            <div className="detail-section">
              <h4 className="detail-section-title">Customer Information</h4>
              {name && (
                <div className="detail-row">
                  <span className="detail-label">Name:</span>
                  <span className="detail-value">{name}</span>
                </div>
              )}
              {email && (
                <div className="detail-row">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{email}</span>
                </div>
              )}
              {phone_number && (
                <div className="detail-row">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{phone_number}</span>
                </div>
              )}
              {!business_name && school && (
                <div className="detail-row">
                  <span className="detail-label">School:</span>
                  <span className="detail-value">{school}</span>
                </div>
              )}
              {organization && (
                <div className="detail-row">
                  <span className="detail-label">Organization:</span>
                  <span className="detail-value">{organization}</span>
                </div>
              )}
              {business_name && (
                <div className="detail-row">
                  <span className="detail-label">Business:</span>
                  <span className="detail-value">{business_name}</span>
                </div>
              )}
              {event_name && (
                <div className="detail-row">
                  <span className="detail-label">Event:</span>
                  <span className="detail-value">{event_name}</span>
                </div>
              )}
              {order_notes && (
                <div className="detail-row">
                  <span className="detail-label">Notes:</span>
                  <span className="detail-value">{order_notes}</span>
                </div>
              )}
            </div>

            {/* Order Information */}
            <div className="detail-section">
              <h4 className="detail-section-title">Order Information</h4>
              {product ? (
                <>
                  <div className="detail-row">
                    <span className="detail-label">Product:</span>
                    <span className="detail-value">
                      {product.brand_name || product.brandName} {product.style}{' '}
                      {product.name}
                    </span>
                  </div>
                  {product_color && product_color.name !== 'Default' && (
                    <div className="detail-row">
                      <span className="detail-label">Color:</span>
                      <span className="detail-value">
                        {titleize(product_color.name)}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className="detail-row">
                  <span className="detail-label">Product:</span>
                  <span className="detail-value">No product selected</span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">Quantity:</span>
                <span className="detail-value">{quantity || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Budget:</span>
                <span className="detail-value">
                  {budget ? `$${budget}` : '-'}
                </span>
              </div>
              {delivery_date && (
                <div className="detail-row">
                  <span className="detail-label">Delivery Date:</span>
                  <span className="detail-value">
                    {formatDate(delivery_date)}
                  </span>
                </div>
              )}
              {design_notes && (
                <div className="detail-row">
                  <span className="detail-label">Design Notes:</span>
                  <span className="detail-value">{design_notes}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DesignPreview;
